import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useChannels,
  useDeleteChannel,
  useActivateChannel,
  useDeactivateChannel,
  useReconnectChannel,
  useChannelsSocket,
} from '../hooks';
import { useSocket } from '@/contexts/SocketContext';
import {
  ChannelCard,
  ChannelsPageSection,
  EmailAccountListItem,
  CreateChannelModal,
  ReconnectOfficialModal,
} from '../components';
import { ChannelListRow, channelTypeLabel } from '../components/ChannelListRow';
import { Button, FiltersButton, ListPageEmpty, ListPageLoading, PageToolbar, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { isEmailChannel } from '../utils/isEmailChannel';
import { mensagemExclusaoCanal } from '../utils/mensagemExclusaoCanal';
import { channelsApi } from '../services';
import { OFFICIAL_META_MESSAGING_PROVIDERS } from '@troia-v3/types';
import type { ChannelQuery, ChannelResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

type SortKey = 'name' | 'type' | 'status';
type StatusFilter = 'active' | 'pending' | 'inactive';
type ViewMode = 'grid' | 'table';

/** Bucket de status usado no filtro/chip (active | pending | inactive). */
function statusBucket(status: string): StatusFilter {
  if (status === 'active') return 'active';
  if (status === 'pending') return 'pending';
  return 'inactive';
}

const STATUS_LABEL: Record<StatusFilter, string> = {
  active: 'Ativo',
  pending: 'Aguardando',
  inactive: 'Inativo',
};

export const ChannelsPage: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [searchTerm, setSearchTerm] = useState('');
  const [query, setQuery] = useState<ChannelQuery>({ page: 1, limit: 50 });
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [reconnectChannelId, setReconnectChannelId] = useState<string | null>(null);
  const [officialReconnect, setOfficialReconnect] = useState<{ id: string; providerId: string } | null>(null);

  const { data, isLoading, error } = useChannels(query);
  useChannelsSocket(); // atualiza card em tempo real (foto, nome, identifier)
  const deleteChannel = useDeleteChannel();
  const activateChannel = useActivateChannel();
  const deactivateChannel = useDeactivateChannel();
  const reconnectChannel = useReconnectChannel();

  const allItems = useMemo(() => data?.items ?? [], [data?.items]);

  // Tipos distintos presentes (para o filtro "Tipo").
  const typeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of allItems) set.add(channelTypeLabel(c.providerId));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [allItems]);

  // Filtros client-side (status + tipo). Busca é server-side via query.search.
  const filteredItems = useMemo(() => {
    return allItems.filter((c) => {
      if (statusFilter && statusBucket(c.status) !== statusFilter) return false;
      if (typeFilter && channelTypeLabel(c.providerId) !== typeFilter) return false;
      return true;
    });
  }, [allItems, statusFilter, typeFilter]);

  const sortFn = useMemo(() => {
    return (a: ChannelResponse, b: ChannelResponse) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'type') cmp = channelTypeLabel(a.providerId).localeCompare(channelTypeLabel(b.providerId), 'pt-BR');
      else {
        const rank = (s: string) => (s === 'active' ? 0 : s === 'pending' ? 1 : 2);
        cmp = rank(a.status) - rank(b.status);
      }
      return sort.dir === 'asc' ? cmp : -cmp;
    };
  }, [sort]);

  const { messagingChannels, emailChannels } = useMemo(() => {
    const messaging: ChannelResponse[] = [];
    const email: ChannelResponse[] = [];
    for (const c of filteredItems) (isEmailChannel(c) ? email : messaging).push(c);
    messaging.sort(sortFn);
    email.sort(sortFn);
    return { messagingChannels: messaging, emailChannels: email };
  }, [filteredItems, sortFn]);

  const sortedAll = useMemo(() => [...filteredItems].sort(sortFn), [filteredItems, sortFn]);

  // ── Seleção ──
  const selectedCount = selectedIds.size;
  const allOnPageSelected = filteredItems.length > 0 && filteredItems.every((c) => selectedIds.has(c.id));
  const someOnPageSelected = filteredItems.some((c) => selectedIds.has(c.id));

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) filteredItems.forEach((c) => next.delete(c.id));
      else filteredItems.forEach((c) => next.add(c.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  // ── Busca / filtros ──
  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  const clearAllFilters = () => {
    setStatusFilter(undefined);
    setTypeFilter(undefined);
    setFiltersOpen(false);
  };

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const statusLabel = statusFilter ? STATUS_LABEL[statusFilter] : null;
  const activeFilterCount = [statusFilter, typeFilter].filter(Boolean).length;

  // ── Ações ──
  const handleDelete = async (id: string) => {
    const workflows = await channelsApi.getActiveWorkflowsUsingChannel(id);
    if (await confirm({ message: mensagemExclusaoCanal(workflows, 1), title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' })) deleteChannel.mutate(id);
  };

  const handleToggleStatus = (id: string, currentStatus: string) => {
    if (currentStatus === 'active') deactivateChannel.mutate(id);
    else activateChannel.mutate(id);
  };

  const handleReconnect = (id: string) => {
    const channel = allItems.find((c) => c.id === id);
    const providerId = channel?.providerId ?? '';
    if ((OFFICIAL_META_MESSAGING_PROVIDERS as readonly string[]).includes(providerId)) {
      setOfficialReconnect({ id, providerId });
      return;
    }
    // gateway / others: mantém o modal de QR existente
    setReconnectChannelId(id);
  };

  // ── Bulk ──
  const bulkActivate = () => {
    allItems.filter((c) => selectedIds.has(c.id) && c.status !== 'active').forEach((c) => activateChannel.mutate(c.id));
    clearSelection();
  };
  const bulkDeactivate = () => {
    allItems.filter((c) => selectedIds.has(c.id) && c.status === 'active').forEach((c) => deactivateChannel.mutate(c.id));
    clearSelection();
  };
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    const workflows = (await Promise.all(ids.map((id) => channelsApi.getActiveWorkflowsUsingChannel(id)))).flat();
    if (!(await confirm({ message: mensagemExclusaoCanal(workflows, selectedCount), title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    Array.from(selectedIds).forEach((id) => deleteChannel.mutate(id));
    clearSelection();
  };

  const renderCheck = (on: boolean, mixed = false, onClick?: () => void, label?: string, testid?: string) => (
    <button
      type="button"
      onClick={onClick}
      className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors flex-shrink-0"
      style={{
        backgroundColor: on || mixed ? 'var(--theme-primary)' : 'transparent',
        borderColor: on || mixed ? 'var(--theme-primary)' : 'var(--theme-input-border)',
        color: '#ffffff',
      }}
      aria-label={label}
      data-testid={testid}
    >
      {on && <Icon name="check" className="w-3 h-3" />}
      {!on && mixed && <span className="w-2 h-0.5 rounded-full bg-white" />}
    </button>
  );

  const SortHead: React.FC<{ label: string; k: SortKey; className?: string }> = ({ label, k, className }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-bold whitespace-nowrap cursor-pointer select-none ${className ?? ''}`}
      style={{ color: 'var(--theme-muted-text)' }}
      onClick={() => handleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === k && <Icon name={sort.dir === 'asc' ? 'chevron-up' : 'chevron-down'} className="w-3 h-3" />}
      </span>
    </th>
  );

  // ── Cards (grid view + mobile do modo Lista) — duas seções ──
  const cardsSections = (
    <>
      <ChannelsPageSection
        icon="message-square"
        title="Mensagens"
        count={messagingChannels.length}
        variant="default"
        data-testid="channels-section-messaging"
      >
        {messagingChannels.length === 0 ? (
          <SectionEmptyPlaceholder icon="message-square" message="Nenhum canal de mensagem" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {messagingChannels.map((channel) => (
              <ChannelCard
                key={channel.id}
                channel={channel}
                members={channel.members}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                onReconnect={handleReconnect}
              />
            ))}
          </div>
        )}
      </ChannelsPageSection>

      <ChannelsPageSection
        icon="mail"
        title="Contas de email"
        count={emailChannels.length}
        variant="email"
        data-testid="channels-section-email"
      >
        {emailChannels.length === 0 ? (
          <SectionEmptyPlaceholder icon="mail" message="Nenhuma conta de email" />
        ) : (
          <div
            className="rounded-lg overflow-hidden"
            style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
          >
            {/* Tabela real (F45) — EmailAccountListItem é <tr>/<td> */}
            <table className="w-full border-collapse">
              <tbody>
                {emailChannels.map((channel) => (
                  <EmailAccountListItem
                    key={channel.id}
                    channel={channel}
                    members={channel.members}
                    selected={selectedIds.has(channel.id)}
                    onToggleSelect={toggleSelect}
                    onDelete={handleDelete}
                    onToggleStatus={handleToggleStatus}
                    onReconnect={handleReconnect}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ChannelsPageSection>
    </>
  );

  const total = data?.total;
  const countPill = activeFilterCount > 0 ? filteredItems.length : total;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={<>
          {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <button onClick={() => navigate('/settings')} className="text-sm font-medium truncate hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }} data-testid="channels-breadcrumb-settings">
              Configurações
            </button>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Canais</span>
          </nav>
          {countPill != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {countPill}
            </span>
          )}
        </>}
        center={
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar canal…"
            data-testid="channels-search"
          />
        }
        right={<>
          {/* Direita: view toggle + Filtros + Novo canal */}
          {/* View toggle */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="channels-view"
          />

          {/* Filtros */}
          <div className="relative flex-none">
            <FiltersButton
              onClick={() => setFiltersOpen((v) => !v)}
              count={activeFilterCount}
              active={filtersOpen}
              data-testid="channels-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                  data-testid="channels-filters-popover"
                >
                  <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
                  {([[undefined, 'Todos'], ['active', 'Ativo'], ['pending', 'Aguardando'], ['inactive', 'Inativo']] as const).map(([val, label]) => {
                    const active = statusFilter === val;
                    return (
                      <button
                        key={label}
                        onClick={() => { setStatusFilter(val); setFiltersOpen(false); }}
                        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                        style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        {label}
                        {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
                      </button>
                    );
                  })}

                  {typeOptions.length > 0 && (
                    <>
                      <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Tipo</p>
                      <button
                        onClick={() => { setTypeFilter(undefined); setFiltersOpen(false); }}
                        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                        style={{ color: '#374151', backgroundColor: !typeFilter ? '#f3e8ff' : 'transparent' }}
                        onMouseEnter={(e) => { if (typeFilter) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { if (typeFilter) e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        Todos
                        {!typeFilter && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
                      </button>
                      {typeOptions.map((t) => {
                        const active = typeFilter === t;
                        return (
                          <button
                            key={t}
                            onClick={() => { setTypeFilter(t); setFiltersOpen(false); }}
                            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <span className="truncate">{t}</span>
                            {active && <Icon name="check" className="w-4 h-4 flex-none" style={{ color: '#8b5cf6' }} />}
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Novo canal (único primário) — Button shared 38px (F01) */}
          <Button
            variant="primary"
            size="md"
            icon="plus"
            collapsibleLabel
            className="flex-none"
            onClick={() => setIsCreateModalOpen(true)}
            data-testid="new-channel"
          >
            Novo canal
          </Button>
        </>}
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => setStatusFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {typeFilter && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Tipo: {typeFilter}
              <button onClick={() => setTypeFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de tipo">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="channels-clear-filters">Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }} data-testid="channels-bulk-bar">
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionado(s)</span>
          <span className="flex-1" />
          <button onClick={bulkActivate} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }} data-testid="channels-bulk-activate">
            <Icon name="power" className="w-3.5 h-3.5" /> Ativar
          </button>
          <button onClick={bulkDeactivate} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }} data-testid="channels-bulk-deactivate">
            <Icon name="power" className="w-3.5 h-3.5" /> Desativar
          </button>
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="channels-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="channels-bulk-cancel">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar canais: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading />}

          {/* Zero canais total */}
          {!isLoading && !error && allItems.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message="Nenhum canal cadastrado"
              description="Crie seu primeiro canal para começar a atender seus clientes"
              actionLabel="Criar primeiro canal"
              onAction={() => setIsCreateModalOpen(true)}
            />
          )}

          {/* Nenhum resultado após busca/filtros */}
          {!isLoading && !error && allItems.length > 0 && filteredItems.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message="Nenhum canal encontrado"
              description="Tente ajustar a busca ou os filtros."
            />
          )}

          {/* Cards (view padrão) */}
          {!isLoading && !error && filteredItems.length > 0 && viewMode === 'grid' && cardsSections}

          {/* Lista (tabela) — vira cards no mobile */}
          {!isLoading && !error && filteredItems.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="md:hidden">{cardsSections}</div>

              {/* Desktop: tabela flat (mensagens + email) */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <th className="pl-4 pr-1 py-3 w-11">
                        {renderCheck(allOnPageSelected, someOnPageSelected && !allOnPageSelected, toggleSelectAllOnPage, 'Selecionar todos', 'channels-select-all')}
                      </th>
                      <SortHead label="Canal" k="name" />
                      <SortHead label="Tipo" k="type" />
                      <th className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Operadores</th>
                      <SortHead label="Status" k="status" />
                      <th className="px-4 py-3 w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAll.map((channel) => (
                      <ChannelListRow
                        key={channel.id}
                        channel={channel}
                        members={channel.members}
                        selected={selectedIds.has(channel.id)}
                        onToggleSelect={toggleSelect}
                        onDelete={handleDelete}
                        onToggleStatus={handleToggleStatus}
                        onReconnect={handleReconnect}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create Channel Modal */}
      <CreateChannelModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          // React Query refetch automático da lista de canais
        }}
      />

      {/* Reconnect Channel Modal (gateway / QR-based providers) */}
      {reconnectChannelId && (
        <ReconnectChannelModal
          channelId={reconnectChannelId}
          isOpen={!!reconnectChannelId}
          onClose={() => setReconnectChannelId(null)}
          reconnectMutation={reconnectChannel}
        />
      )}

      {/* Reconnect Official Modal (whatsapp-business, facebook-messenger, instagram-messaging) */}
      {officialReconnect && (
        <ReconnectOfficialModal
          channelId={officialReconnect.id}
          providerId={officialReconnect.providerId}
          isOpen={!!officialReconnect}
          onClose={() => setOfficialReconnect(null)}
        />
      )}
    </div>
  );
};

interface SectionEmptyPlaceholderProps {
  icon: IconName;
  message: string;
}

const SectionEmptyPlaceholder: React.FC<SectionEmptyPlaceholderProps> = ({ icon, message }) => (
  <div
    className="rounded-lg flex flex-col items-center justify-center py-10 px-6"
    style={{ border: '1px dashed var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
  >
    <Icon name={icon} className="w-8 h-8 mb-2" style={{ opacity: 0.5 }} />
    <span style={{ fontSize: 13 }}>{message}</span>
  </div>
);

// Simple Reconnect Modal Component (gateway / QR)
interface ReconnectChannelModalProps {
  channelId: string;
  isOpen: boolean;
  onClose: () => void;
  reconnectMutation: ReturnType<typeof useReconnectChannel>;
}

const ReconnectChannelModal: React.FC<ReconnectChannelModalProps> = ({
  channelId,
  isOpen,
  onClose,
  reconnectMutation,
}) => {
  const { socket, isConnected: socketConnected } = useSocket();
  const queryClient = useQueryClient();
  const [qrCodeData, setQrCodeData] = React.useState<{
    qrCode?: string;
    message?: string;
    instanceKey?: string;
  }>({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [isConnected, setIsConnected] = React.useState(false);
  const hasReconnectedRef = React.useRef(false);

  React.useEffect(() => {
    if (isOpen && channelId && !hasReconnectedRef.current) {
      hasReconnectedRef.current = true;
      setIsConnected(false);
      void handleReconnect();
    }
    if (!isOpen) {
      hasReconnectedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, channelId]);

  React.useEffect(() => {
    if (!qrCodeData.instanceKey || !socket || !socketConnected) return;

    const connectEvent = `channel:${qrCodeData.instanceKey}:connect`;
    const disconnectEvent = `channel:${qrCodeData.instanceKey}:disconnect`;

    const handleConnect = () => {
      setIsConnected(true);
      setIsLoading(false);
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      setTimeout(() => { onClose(); }, 2000);
    };

    const handleDisconnect = () => {
      setIsConnected(false);
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    };

    socket.on(connectEvent, handleConnect);
    socket.on(disconnectEvent, handleDisconnect);

    return () => {
      socket.off(connectEvent, handleConnect);
      socket.off(disconnectEvent, handleDisconnect);
    };
  }, [qrCodeData.instanceKey, socket, socketConnected, onClose, queryClient]);

  const handleReconnect = async () => {
    setIsLoading(true);
    try {
      const result = await reconnectMutation.mutateAsync({ id: channelId });
      setQrCodeData({
        qrCode: result.qrCode,
        message: result.message,
        instanceKey: result.instanceKey || channelId,
      });
    } catch {
      setQrCodeData({ message: 'Erro ao gerar QR Code de reconexão' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal((
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      /* pareamento em andamento: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
      data-testid="channel-reconnect-modal"
    >
      <div
        className="relative w-full max-w-md rounded-lg shadow-xl p-6"
        style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold" style={{ color: '#1e293b' }}>Reconectar Canal</h2>
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#1e293b'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
            aria-label="Fechar"
          >
            <Icon name="x" className="w-4 h-4" />
          </button>
        </div>

        {isLoading && (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
            <p className="mt-4 text-sm" style={{ color: '#64748b' }}>Gerando QR Code...</p>
          </div>
        )}

        {!isLoading && isConnected && (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Icon name="check-circle" className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: '#1e293b' }}>Canal Conectado!</h3>
            <p className="text-sm text-center" style={{ color: '#64748b' }}>O canal foi reconectado com sucesso.</p>
          </div>
        )}

        {!isLoading && !isConnected && qrCodeData.qrCode && (
          <div className="flex flex-col items-center">
            <div className="bg-white p-4 rounded-lg mb-4" style={{ border: '1px solid #e5e7eb' }}>
              <img src={qrCodeData.qrCode} alt="QR Code de Reconexão" className="w-64 h-64" />
            </div>
            <p className="text-sm text-center mb-4" style={{ color: '#64748b' }}>
              Escaneie este QR Code com o WhatsApp para reconectar o canal
            </p>
          </div>
        )}

        {!isLoading && !isConnected && !qrCodeData.qrCode && qrCodeData.message && (
          <div className="p-4 rounded-lg text-center" style={{ backgroundColor: 'rgba(248, 113, 113, 0.1)', color: '#ef4444' }}>
            <p>{qrCodeData.message}</p>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: '#8b5cf6', color: '#ffffff' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#7c3aed'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#8b5cf6'; }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  ), document.body);
};
